import React, { useState } from 'react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@mantis-sas/ui";
import {
  AlertTriangle,
  Trash2,
  X,
  Loader2,
  User,
  Building2,
  Mail,
  Phone
} from 'lucide-react';

import { useClients } from '../Utils/useClients';

export const DeleteClientModal = ({
  isOpen,
  onClose,
  onConfirm,
  onLoading,
  client,
  clientId // Will get this from props or context
}) => {
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  
  const { deleteClient } = useClients(clientId);

  if (!client) return null;

  const handleDelete = async () => {
    try {
      setDeleteError('');
      setIsDeleting(true);
      onLoading?.(true);
      
      const clientCustomId = client.id || client._id || client.customClientId;
      if (!clientCustomId) {
        throw new Error('No se encontró el ID del cliente');
      }
      
      await deleteClient(clientCustomId, {
        user_name: 'current_user', // This should come from auth context
        reason: 'Eliminado desde la interfaz de usuario'
      });
      
      onConfirm?.();
    } catch (error) {
      setDeleteError(error.message || 'Error al eliminar el cliente');
      onLoading?.(false);
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      setDeleteError('');
      onClose();
    }
  };

  const getInitials = (name) => {
    if (!name) return 'C';
    return name.split(' ').map(word => word.charAt(0)).join('').slice(0, 2).toUpperCase();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2 text-xl text-red-600 dark:text-red-400">
            <AlertTriangle className="h-6 w-6" />
            <span>Confirmar Eliminación</span>
          </DialogTitle>
          <DialogDescription>
            Esta acción no se puede deshacer. El cliente será eliminado permanentemente.
          </DialogDescription>
        </DialogHeader>

        {deleteError && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{deleteError}</AlertDescription>
          </Alert>
        )}

        {/* Client Information Preview */}
        <div className="bg-slate-50 dark:bg-slate-800 rounded p-4 border border-slate-200/60 dark:border-slate-700/60">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-md">
              {getInitials(client.name)}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                {client.name || 'Sin nombre'}
              </h3>
              <div className="flex items-center space-x-2 mt-1">
                {client.is_company ? (
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700">
                    <Building2 className="h-3 w-3 mr-1" />
                    Empresa
                  </Badge>
                ) : (
                  <Badge className="bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700">
                    <User className="h-3 w-3 mr-1" />
                    Persona
                  </Badge>
                )}
                {client.status && (
                  <Badge 
                    variant="outline" 
                    className={`text-xs ${
                      client.status === 'active' 
                        ? 'border-green-300 text-green-700 dark:border-green-700 dark:text-green-300'
                        : client.status === 'inactive'
                        ? 'border-red-300 text-red-700 dark:border-red-700 dark:text-red-300'
                        : 'border-yellow-300 text-yellow-700 dark:border-yellow-700 dark:text-yellow-300'
                    }`}
                  >
                    {client.status === 'active' ? 'Activo' : 
                     client.status === 'inactive' ? 'Inactivo' : 'Pendiente'}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2 text-sm">
            {client.email && (
              <div className="flex items-center space-x-2 text-slate-600 dark:text-slate-400">
                <Mail className="h-4 w-4 text-slate-400" />
                <span className="truncate">{client.email}</span>
              </div>
            )}
            {client.phone && (
              <div className="flex items-center space-x-2 text-slate-600 dark:text-slate-400">
                <Phone className="h-4 w-4 text-slate-400" />
                <span className="truncate">{client.phone}</span>
              </div>
            )}
            {client.country && (
              <div className="text-slate-600 dark:text-slate-400">
                📍 {client.country}{client.city ? `, ${client.city}` : ''}
              </div>
            )}
            {client.tax_id && (
              <div className="text-slate-600 dark:text-slate-400">
                NIT: {client.tax_id}
              </div>
            )}
          </div>
        </div>

        {/* Warning Message */}
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded p-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="space-y-2">
              <h4 className="font-semibold text-red-800 dark:text-red-200">
                ¿Está seguro que desea eliminar este cliente?
              </h4>
              <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
                <li>• Se eliminará toda la información del cliente</li>
                <li>• Se perderá el historial de interacciones</li>
                <li>• Esta acción no se puede deshacer</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isDeleting}
            className="border-slate-200/60 dark:border-slate-700/60 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          <Button
            onClick={handleDelete}
            disabled={isDeleting}
            variant="destructive"
            className="bg-red-600 hover:bg-red-700 text-white shadow-lg"
          >
            {isDeleting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            {isDeleting ? 'Eliminando...' : 'Eliminar Cliente'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 